import React, { useCallback, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Button, { ButtonToneProps } from 'src/components/ui/Button/Button';
import { useOverlayNav } from 'src/providers/OverlayNavProvider';

const OverlayNavArrows = () => {
  const nav = useOverlayNav();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const navigateTo = useCallback(
    (direction: 'prev' | 'next') => {
      if (!nav || nav.entityIds.length === 0) return;
      const currentIndex = nav.entityIds.findIndex(eid => String(eid) === String(id));
      if (currentIndex === -1) return;

      const nextIndex =
        direction === 'prev'
          ? (currentIndex - 1 + nav.entityIds.length) % nav.entityIds.length
          : (currentIndex + 1) % nav.entityIds.length;

      const doNavigate = () => navigate(`${nav.basePath}/${nav.entityIds[nextIndex]}`);

      if (nav.beforeNavigateRef.current) {
        nav.beforeNavigateRef.current(doNavigate);
      } else {
        doNavigate();
      }
    },
    [nav, id, navigate],
  );

  const navigateToRef = useRef(navigateTo);
  navigateToRef.current = navigateTo;

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;

      const tag = (e.target as HTMLElement)?.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select') return;
      if ((e.target as HTMLElement)?.isContentEditable) return;

      e.preventDefault();
      navigateToRef.current(e.key === 'ArrowUp' ? 'prev' : 'next');
    };

    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  if (!nav || nav.entityIds.length === 0) return null;

  const currentIndex = nav.entityIds.findIndex(eid => String(eid) === String(id));
  if (currentIndex === -1) return null;

  return (
    <div className="flex-1 flex justify-center items-center gap-x-4">
      <Button
        icon="arrowUpward"
        tone={ButtonToneProps.SECONDARY}
        hoverText={t('overlay_nav.prev_tooltip')}
        onClick={() => navigateTo('prev')}
      />
      <Button
        icon="arrowDownward"
        tone={ButtonToneProps.SECONDARY}
        hoverText={t('overlay_nav.next_tooltip')}
        onClick={() => navigateTo('next')}
      />
    </div>
  );
};

export default OverlayNavArrows;
